// Contract 29 §9 — the wizard's driver. Owns step navigation and the two journey flows; the
// components own copy and rendering only.

import { computed, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'

import { OnboardingApi } from '../api/OnboardingApi'
import { CompleteOnboardingFlow } from '../flows/CompleteOnboardingFlow'
import { CreateWorkspaceFlow, type CreateWorkspaceResult } from '../flows/CreateWorkspaceFlow'
import { ONBOARDING_STEPS, type OnboardingStepKey } from '../models/onboarding.types'
import { isLastStep, nextStepKey, previousStepKey, stepNumber } from '../service/onboardingSteps'
import { useOnboardingStore } from '../stores/onboardingStore'

import { useOnboardingAnalytics } from './useOnboardingAnalytics'

import { FREE_ACCESS_PACKAGE_IDS, useAccount, useWorkspace } from '@/features/account'

export interface UseOnboarding {
  step: ComputedRef<OnboardingStepKey>
  steps: readonly OnboardingStepKey[]
  furthestStep: ComputedRef<number>
  canRevisit: (step: OnboardingStepKey) => boolean
  isLast: ComputedRef<boolean>
  goTo: (step: OnboardingStepKey) => Promise<void>
  next: () => Promise<void>
  connectMeta: () => Promise<void>
  back: () => Promise<void>
  createWorkspace: (input: {
    name: string
    domain: string
    mcpOnly?: boolean
  }) => Promise<CreateWorkspaceResult>
  saveBusinessType: (value: string) => Promise<void>
  saveHeardAbout: (
    source: import('../models/onboarding.types').HeardAboutSource,
    other?: string,
  ) => Promise<void>
  finish: () => Promise<void>
}

export function useOnboarding(): UseOnboarding {
  const router = useRouter()
  const store = useOnboardingStore()
  const account = useAccount()
  const workspace = useWorkspace()
  const analytics = useOnboardingAnalytics()
  const api = new OnboardingApi()

  /** The takeover's own position (v3 `layoutStore.onboardingStep`) — no longer the URL. */
  const step = computed<OnboardingStepKey>(() => store.step)

  async function goTo(target: OnboardingStepKey): Promise<void> {
    if (target === step.value) return
    // Forward navigation is only ever reached through `next()`, which advances the clamp first.
    if (!store.canRevisit(target)) return
    analytics.trackStepLeave(step.value)
    store.setStep(target)
    analytics.trackStepEnter(target)
  }

  async function next(): Promise<void> {
    if (isLastStep(step.value)) return
    const target = nextStepKey(step.value)
    store.advanceTo(target)
    await goTo(target)
  }

  async function connectMeta(): Promise<void> {
    // An intentional jump: connecting Meta skips the generic business-type prompt.
    analytics.trackStepLeave(step.value)
    store.setStep('connect')
    analytics.trackStepEnter('connect')
  }

  async function back(): Promise<void> {
    if (stepNumber(step.value) === 1) return
    await goTo(previousStepKey(step.value))
  }

  async function createWorkspace(input: {
    name: string
    domain: string
    mcpOnly?: boolean
  }): Promise<CreateWorkspaceResult> {
    const existingWorkspace = workspace.workspace.value
    const isShopifyFreeWorkspace =
      account.accountDetail.value?.user?.is_shopify === true &&
      account.packageId.value !== null &&
      FREE_ACCESS_PACKAGE_IDS.has(account.packageId.value) &&
      existingWorkspace !== null
    const flow = new CreateWorkspaceFlow({
      accountId: () => account.accountId.value,
      refreshWorkspaces: async () => {
        await account.loadSelectedAccountContext(true)
      },
      selectWorkspace: async (workspaceId) => {
        const target = workspace.workspaces.value.find(
          (ws) => String(ws.id) === String(workspaceId),
        )
        if (target) await workspace.selectWorkspace(target)
      },
      setDefaults: (defaults) => store.setDefaults(defaults),
      setCreatedWorkspace: (id) => store.setCreatedWorkspace(id),
      isGoogleFirstWorkspace: () =>
        account.user.value?.user?.is_google === true &&
        workspace.workspaces.value.length === 0 &&
        String(account.account.value?.name ?? '') === String(account.account.value?.user_id ?? ''),
      report: (scope, error) => console.warn(`[global-surface] ${scope} failed`, error),
    })
    const result = await flow.execute(
      isShopifyFreeWorkspace && existingWorkspace
        ? { ...input, reuseWorkspaceId: Number(existingWorkspace.id) }
        : input,
    )
    if (result.ok) await next()
    return result
  }

  async function saveBusinessType(value: string): Promise<void> {
    const accountId = account.accountId.value
    if (accountId !== undefined) await api.saveBusinessType(accountId, value)
  }

  async function saveHeardAbout(
    source: import('../models/onboarding.types').HeardAboutSource,
    other?: string,
  ): Promise<void> {
    const accountId = account.accountId.value
    if (accountId !== undefined) await api.saveHeardAbout(accountId, source, other)
  }

  async function finish(): Promise<void> {
    await new CompleteOnboardingFlow({
      trackCompleted: () => analytics.trackCompleted(step.value),
      // v3 `handleFinish`: the takeover closes, its seed and position with it.
      resetOnboarding: () => store.close(),
      reloadContext: () => account.loadSelectedAccountContext(true),
      navigate: (to) => router.push(to),
      report: (scope, error) => console.warn(`[global-surface] ${scope} failed`, error),
    }).execute()
  }

  return {
    step,
    steps: ONBOARDING_STEPS,
    furthestStep: computed(() => store.furthestStep),
    canRevisit: (target) => store.canRevisit(target),
    isLast: computed(() => isLastStep(step.value)),
    goTo,
    next,
    connectMeta,
    back,
    createWorkspace,
    saveBusinessType,
    saveHeardAbout,
    finish,
  }
}
